import { forwardRef, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { Briefcase, ClipboardList, FileSignature, ListTodo, PackageOpen, Search, Tag, Wrench, X } from 'lucide-react'
import client from '../api/client'
import { Spinner } from './ui'

interface SearchItem { id: string; title: string; subtitle: string | null; url: string }
interface SearchGroup { key: string; label: string; items: SearchItem[]; has_more: boolean; more_url: string | null }

const GROUP_ICON: Record<string, React.ReactNode> = {
  orders: <Briefcase size={14} />,
  protocols: <FileSignature size={14} />,
  preparations: <ClipboardList size={14} />,
  products: <Tag size={14} />,
  tools: <Wrench size={14} />,
  tasks: <ListTodo size={14} />,
}

/**
 * Globální fulltext v horní liště (vzor Serviqo): zakázky, protokoly, přípravy, produkty,
 * nářadí a úkoly podle oprávnění, bez ohledu na diakritiku. Šipky vybírají, Enter otevře,
 * Esc zavře; Ctrl+K zaostří pole odkudkoli (řeší layout).
 */
export const GlobalSearch = forwardRef<HTMLInputElement, { onNavigate?: () => void }>(function GlobalSearch({ onNavigate }, ref) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const wrap = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 250)
    return () => window.clearTimeout(t)
  }, [q])

  const { data, isFetching } = useQuery({
    queryKey: ['global-search', debounced],
    queryFn: async () => (await client.get<{ data: { groups: SearchGroup[] } }>('/search', { params: { q: debounced } })).data.data,
    enabled: debounced.length >= 2,
    staleTime: 30_000,
  })
  const groups = debounced.length >= 2 ? data?.groups ?? [] : []
  const flat = groups.flatMap((g) => g.items.map((it) => ({ key: `${g.key}-${it.id}`, url: it.url })))
  useEffect(() => setActive(0), [debounced, data])

  // Klik mimo zavře výsledky.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const go = (url: string) => {
    setOpen(false)
    setQ('')
    onNavigate?.()
    navigate(url)
  }
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { setOpen(false); e.currentTarget.blur(); return }
    if (e.key === 'Enter') {
      e.preventDefault()
      const url = flat[Math.min(active, flat.length - 1)]?.url
      if (url) go(url)
      else if (q.trim()) go(`/attendance/orders?q=${encodeURIComponent(q.trim())}`)
      return
    }
    if (flat.length === 0 || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return
    e.preventDefault()
    setOpen(true)
    setActive((a) => (e.key === 'ArrowDown' ? (a + 1) % flat.length : (a - 1 + flat.length) % flat.length))
  }
  useEffect(() => {
    wrap.current?.querySelector('[data-search-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active])

  const showPanel = open && debounced.length >= 2

  return (
    <div ref={wrap} className="relative w-full min-w-0 flex-1">
      <div role="search" className="flex h-10 items-center gap-2.5 rounded-full bg-white px-4 shadow-sm ring-1 ring-slate-900/[0.06] transition focus-within:ring-2 focus-within:ring-brand-500/30">
        <Search size={16} className="shrink-0 text-ink-400" aria-hidden />
        <input
          ref={ref}
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          role="combobox"
          aria-expanded={showPanel && flat.length > 0}
          aria-controls="global-search-results"
          aria-autocomplete="list"
          aria-label="Hledat v celé aplikaci"
          placeholder="Hledat zakázku, přípravu, nářadí, produkt…"
          className="w-full min-w-0 bg-transparent text-base text-ink-800 outline-none placeholder:text-ink-400 sm:text-sm [&::-webkit-search-cancel-button]:hidden"
        />
        {isFetching ? <Spinner className="!h-4 !w-4 shrink-0" /> : q ? (
          <button type="button" onClick={() => setQ('')} aria-label="Vymazat hledání" className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-ink-400 hover:bg-slate-100 hover:text-ink-700"><X size={14} /></button>
        ) : (
          <kbd className="hidden shrink-0 rounded-md border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold text-ink-400 lg:inline" title="Hledání odkudkoli: Ctrl+K">Ctrl K</kbd>
        )}
      </div>

      {showPanel && (
        <div id="global-search-results" role="listbox" className="scrollbar-thin absolute inset-x-0 top-full z-[60] mt-1.5 max-h-[65vh] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-soft-lg">
          {groups.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-ink-500">{isFetching || debounced !== q.trim() ? 'Hledám…' : `Pro „${debounced}“ nebylo nic nalezeno.`}</p>
          ) : groups.map((g) => (
            <div key={g.key} className="mb-1 last:mb-0">
              <p className="flex items-center gap-1.5 px-3 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wide text-ink-500">
                <span className="text-brand-600">{GROUP_ICON[g.key] ?? <PackageOpen size={14} />}</span> {g.label}
              </p>
              {g.items.map((it) => {
                const key = `${g.key}-${it.id}`
                const isActive = flat[active]?.key === key
                return (
                  <button
                    key={key}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    data-search-active={isActive || undefined}
                    onClick={() => go(it.url)}
                    onMouseEnter={() => setActive(flat.findIndex((f) => f.key === key))}
                    className={clsx('flex w-full min-w-0 flex-col items-stretch overflow-hidden rounded-xl px-3 py-2 text-left transition', isActive ? 'bg-brand-50' : 'hover:bg-slate-50')}
                  >
                    <span className="block w-full truncate text-sm font-semibold text-ink-900">{it.title}</span>
                    {it.subtitle && <span className="block w-full truncate text-xs text-ink-500">{it.subtitle}</span>}
                  </button>
                )
              })}
              {g.has_more && g.more_url && (
                <button type="button" onClick={() => go(g.more_url!)} className="w-full rounded-xl px-3 py-1.5 text-left text-xs font-semibold text-brand-700 hover:bg-brand-50">
                  Zobrazit všechny výsledky v seznamu →
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
})
